import { PreviewDataset, PreviewEmployee, PreviewTraining } from '../preview.types';

// The "Show my team" dataset builder.
//
// UNLIKE the synthetic fixtures (aviation, …) this dataset is built from the authenticated
// training manager's OWN students — real names + their completed courses. It is therefore the one
// deliberate exception to preview.types' "100% synthetic" rule, bounded by ADR 0001 Amendment 1:
// the data is read in the UN-ARMED bootstrap phase (so the fail-closed mock is never holed), it is
// the viewer's own authorized data, and it never contaminates a synthetic fixture.
//
// The shape is intentionally minimal so the real /team renders to the PO's spec:
//   • no jobs            ⇒ employees are jobless (jobId omitted) ⇒ hasJob:false ⇒ no job-match bar
//   • no attributeDefs   ⇒ Competencies tab renders EMPTY ("nothing here yet") — the point is to
//                          show the prospect they haven't mapped competencies yet
//   • no tasks           ⇒ Tasks tab renders EMPTY, same story
//   • completion is EXPLICIT (completedTrainingIds), not competency-derived ⇒ gaps are empty by
//     construction (there is nothing to gap against), exactly as required.
//
// The Training tab is DIFFERENT: it is populated from the org's real course catalog (all courses,
// "similar to what's real") so the demo shows recognisable data, with the team's real completion
// counts overlaid per course.

export const MY_TEAM_DATASET_CODE = 'my-team';

/** A managed student — names only (the only PII the my-team view shows). */
export interface MyTeamStudent {
  employeeId: string;
  firstName: string;
  lastName: string;
}

/** A course in the org's training catalog — these ARE the Training-tab rows in my-team mode. */
export interface MyTeamCatalogCourse {
  /** Internal course id (TrainingManagerCourseDto.Id). A completion's `courseId` joins on this. */
  id: string;
  /** ReferenceCode/SKU — the fallback join to a completion's `courseSourceId`. */
  sku?: string;
  courseName: string;
  /** Integration label shown after the course name (the LMS name, e.g. "LearnUpon"/"Ceventas"). */
  integrationAbbreviation?: string;
}

/** A course a student has COMPLETED. Both ids are optional; at least one is present. */
export interface MyTeamCompletedCourse {
  /** Internal course id (Guid) when known — the primary join to the catalog. */
  courseId?: string;
  /** LMS source id / SKU — fallback join, and the row key when the course isn't in the catalog. */
  courseSourceId?: string;
  courseName: string;
}

/** One student paired with the courses they have COMPLETED. */
export interface MyTeamStudentCompletions {
  employeeId: string;
  completedCourses: MyTeamCompletedCourse[];
}

/**
 * Builds the my-team PreviewDataset from the TM's students, the org's course catalog, and each
 * student's completed courses. Pure — no I/O, no global state — so it is fully unit-testable and
 * the fetch layer can be wired independently. `orgName` is the prospect's real org name (chrome).
 *
 * The whole catalog becomes the Training-tab rows; completions are matched onto those rows (by
 * internal id, then SKU) so each course shows the team's real completed count. A completed course
 * that isn't in the catalog is appended as its own row so no completion is silently dropped.
 */
export function buildMyTeamDataset(
  students: MyTeamStudent[],
  completions: MyTeamStudentCompletions[],
  catalog: MyTeamCatalogCourse[],
  orgName: string
): PreviewDataset {
  // A Training row is jobless/competency-free: completion is stated explicitly per employee, so it
  // carries no competency framework. One shape, one place.
  const toTrainingRow = (id: string, name: string, abbreviation?: string): PreviewTraining => ({
    id,
    name,
    integrationAbbreviation: abbreviation ?? '',
    competencyIds: [],
  });

  const trainingsById = new Map<string, PreviewTraining>();
  const trainingIdBySku = new Map<string, string>();
  for (const course of catalog) {
    if (trainingsById.has(course.id)) continue;
    trainingsById.set(
      course.id,
      toTrainingRow(course.id, course.courseName, course.integrationAbbreviation)
    );
    if (course.sku) trainingIdBySku.set(course.sku, course.id);
  }

  // Resolve a completed course to a training-row id: prefer the internal id (equals the catalog id
  // on both the Ceventas and LearnUpon paths), fall back to the SKU. If neither matches a catalog
  // row, append the completion as its own row (keyed by whichever id it carries).
  const resolveTrainingId = (c: MyTeamCompletedCourse): string | null => {
    if (c.courseId && trainingsById.has(c.courseId)) return c.courseId;
    if (c.courseSourceId && trainingIdBySku.has(c.courseSourceId))
      return trainingIdBySku.get(c.courseSourceId)!;
    const orphanKey = c.courseId ?? c.courseSourceId;
    if (!orphanKey) return null;
    if (!trainingsById.has(orphanKey)) {
      trainingsById.set(orphanKey, toTrainingRow(orphanKey, c.courseName));
    }
    return orphanKey;
  };

  const completedIdsByEmployee = new Map<string, string[]>();
  for (const { employeeId, completedCourses } of completions) {
    const ids = new Set<string>();
    for (const course of completedCourses) {
      const id = resolveTrainingId(course);
      if (id) ids.add(id);
    }
    completedIdsByEmployee.set(employeeId, Array.from(ids));
  }

  const employees: PreviewEmployee[] = students.map((s) => ({
    id: s.employeeId,
    firstName: s.firstName,
    lastName: s.lastName,
    // jobId omitted ⇒ jobless. location blank — there is no location lens in this view.
    location: '',
    attributes: [],
    completedTrainingIds: completedIdsByEmployee.get(s.employeeId) ?? [],
  }));

  return {
    code: MY_TEAM_DATASET_CODE,
    companyName: orgName,
    industry: '',
    attributeDefinitions: [],
    jobs: [],
    employees,
    tasks: [],
    trainings: Array.from(trainingsById.values()),
  };
}
